import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { Seo, Breadcrumbs, breadcrumbSchema, faqSchema } from "@/components/Seo";
import { Reveal, SectionTag } from "@/components/Motion";
import { ChatButton } from "@/components/Cta";
import FaqAccordion from "@/components/FaqAccordion";

const SLUG = "tiktok-shop-dropshipping-malaysia";
const PUBLISHED = "2026-08-26";
const UPDATED = "2026-08-26";

const TOC = [
  "什么是 TikTok Shop 一件代发？",
  "一件代发和传统囤货有什么不同？",
  "哪些商家可能适合了解一件代发？",
  "哪些情况不一定适合？",
  "TikTok Shop 一件代发通常怎样运作？",
  "无需囤货是不是完全不用资金？",
  "一件代发最重要的不是「没有库存」",
  "TikTok Shop 一件代发有哪些风险？",
  "怎样判断产品适不适合 TikTok Shop？",
  "一件代发和代运营有什么区别？",
  "TKShopMY 可以协助什么？",
];

const COMPARISON = {
  headers: ["比较项目", "一件代发", "传统囤货"],
  rows: [
    ["库存压力", "较低，库存通常由供应或履约方管理", "较高，需要预先采购库存"],
    ["前期准备", "较轻，可以先测试少量产品", "较重，需要资金和仓储准备"],
    ["仓储", "通常不需要自己的仓库", "需要仓储空间或第三方仓库"],
    ["订单履约", "由合作方按订单安排发货", "自己或团队负责打包发货"],
    ["产品控制", "较低，品质和包装由供应方执行", "较高，全流程自己掌控"],
    ["利润空间", "通常较薄，履约成本包含在合作价内", "采购量大时毛利空间较好"],
    ["供应稳定度", "依赖供应商库存与配合", "自己掌握库存节奏"],
    ["运营复杂度", "需要做好库存同步与售后协调", "履约简单直接，但管理环节多"],
  ],
};

const SUITABLE = [
  { t: "刚开始做电商", d: "还没有完整库存和团队，想先用较轻的方式开始。" },
  { t: "已有实体店", d: "想测试新的线上销售渠道，又不想马上压一批货。" },
  { t: "Shopee / Lazada 卖家", d: "已经有电商经验，希望增加 TikTok Shop 渠道。" },
  { t: "品牌或产品供应商", d: "希望用较低成本测试新产品和新市场反应。" },
  { t: "不想一次投入大量库存", d: "希望先用较轻的方式了解市场，再决定是否扩大。" },
];

const NOT_SUITABLE = [
  "产品供应不稳定，供应商经常缺货",
  "利润过低，扣掉履约成本后没有空间",
  "售后复杂的产品（例如需要安装、调试）",
  "产品容易缺货或季节性太强",
  "物流时效难以控制",
  "平台限制较多的产品类别",
  "需要高度定制的产品",
];

const STEPS = [
  { t: "产品评估", d: "先确认产品、价格、利润空间和目标客户，不要盲目开始。" },
  { t: "确认供应与履约", d: "明确库存、出货、物流、售后由谁负责，写成清楚的安排。" },
  { t: "建立 TikTok Shop", d: "准备店铺资料与商品页面，把基础设置一次做对。" },
  { t: "内容与推广", d: "根据产品制作短视频、直播或其他内容，让产品有被发现的机会。" },
  { t: "订单与售后", d: "订单产生后，按照合作方式安排履约和售后，并持续复盘。" },
];

const KEY_POINTS = ["供应是否稳定", "发货是否及时", "产品质量", "售后安排", "库存同步", "利润结构", "内容是否适合 TikTok", "运营团队是否能跟上"];

const RISKS = [
  "供应商临时缺货，订单发不出去",
  "发货速度不受自己控制，影响店铺评分",
  "不同批次产品品质不一致",
  "退货处理链条更长",
  "库存不同步，页面显示有货但实际没货",
  "履约成本叠加，利润被压缩",
  "客户体验难以完全掌控",
  "平台规则变化影响合作模式",
  "选到违规或受限产品的风险",
];

const PRODUCT_FACTORS = ["产品是否容易展示", "有没有视觉吸引力", "价格是否适合线上消费", "利润空间", "内容素材是否容易取得", "目标客户是谁", "竞争情况", "供应能力", "履约能力"];

const FAQS = [
  { q: "TikTok Shop 可以一件代发吗？", a: "根据实际产品、供应、履约安排和平台规则而定。需要先确认具体合作方式，不能直接假设所有产品都可以。" },
  { q: "一件代发需要自己囤货吗？", a: "部分模式可以不需要卖家自己长期囤大量库存，但库存仍可能由供应或履约方管理，具体安排要在合作前确认清楚。" },
  { q: "一件代发适合新手吗？", a: "部分新卖家会考虑这种方式，但仍需要了解产品、成本、内容、物流和售后。「没有库存」不等于「没有要学习的东西」。" },
  { q: "一件代发是不是零成本？", a: "不是。平台相关成本、内容、运营、广告、样品、物流和售后都是真实存在的成本，开始前应完整计算。" },
  { q: "已经有 Shopee，可以同时做 TikTok Shop 吗？", a: "可以根据业务情况考虑增加渠道，但需要评估库存、团队和运营能力是否跟得上两个平台。" },
  { q: "TKShopMY 有提供一件代发吗？", a: "TKShopMY 提供相关合作模式咨询及 TikTok Shop 开店、商品、内容和运营支持。具体适合哪一种方式，需要根据商家和产品情况确认。" },
  { q: "没有电商经验可以开始吗？", a: "可以先了解流程，再决定是否适合开始。TKShopMY 可以先帮你评估产品和情况。" },
];

function Section({ id, num, title, children }) {
  return (
    <section id={id} className="scroll-mt-28">
      <h2 className="font-sans text-2xl font-bold leading-snug tracking-tight text-ink sm:text-3xl">
        <span className="mr-3 font-display font-light italic text-moss">{num}</span>
        {title}
      </h2>
      <div className="mt-4 space-y-4 text-base leading-relaxed text-ink/70">{children}</div>
    </section>
  );
}

export default function BlogDropshipping() {
  const crumbs = [
    { name: "首页", path: "/" },
    { name: "电商资讯", path: "/blog" },
    { name: "TikTok Shop 一件代发马来西亚完整指南", path: `/blog/${SLUG}` },
  ];

  return (
    <>
      <Seo
        title="TikTok Shop 一件代发马来西亚怎么做？无需囤货模式完整指南 | TKShopMY"
        description="想了解 TikTok Shop 一件代发和无需自行囤货模式？TKShopMY 整理马来西亚商家开始 TikTok Shop dropshipping 前需要了解的流程、适合对象、运营方式和常见问题。"
        path={`/blog/${SLUG}`}
        schemas={[
          breadcrumbSchema(crumbs),
          faqSchema(FAQS),
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "TikTok Shop 一件代发马来西亚怎么开始？",
            description: "马来西亚商家开始 TikTok Shop 一件代发与无需囤货模式前需要了解的流程、比较、风险与常见问题。",
            author: { "@type": "Organization", name: "TKShopMY", url: "https://tkshopmy.com" },
            publisher: { "@id": "https://tkshopmy.com/#organization" },
            datePublished: PUBLISHED,
            dateModified: UPDATED,
            mainEntityOfPage: `https://tkshopmy.com/blog/${SLUG}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            url: `https://tkshopmy.com/blog/${SLUG}`,
            name: "TikTok Shop 一件代发马来西亚怎么做？无需囤货模式完整指南",
            isPartOf: { "@id": "https://tkshopmy.com/#website" },
            about: { "@id": "https://tkshopmy.com/#organization" },
          },
        ]}
      />
      <article className="pt-28 md:pt-36 pb-16 md:pb-24" data-testid="blog-dropshipping">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <Breadcrumbs items={crumbs} />
          <Reveal>
            <h1 className="font-sans text-3xl font-bold leading-[1.2] tracking-tight text-balance text-ink sm:text-4xl lg:text-5xl">
              TikTok Shop 一件代发马来西亚怎么开始？
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink/50">
              <span>作者：<span className="font-semibold text-ink/70">TKShopMY 团队</span></span>
              <span>发布于 {PUBLISHED}</span>
              <span>更新于 {UPDATED}</span>
              <span>阅读约 12 分钟</span>
            </div>
            <p className="mt-7 text-base leading-relaxed text-ink/70 md:text-lg">
              很多马来西亚商家对 TikTok Shop 有兴趣，但不一定希望一开始就准备大量库存、仓库和完整运营团队。
              因此，一件代发（TikTok Shop dropshipping Malaysia）或无需自行囤货的合作模式，成为部分新卖家、
              现有电商卖家和品牌测试新渠道时会考虑的方式。
            </p>
            <p className="mt-4 text-base leading-relaxed text-ink/70">
              但「一件代发」并不等于零成本、零风险或保证有订单。开始之前，仍然需要了解产品、供应、履约、
              平台规则、内容和售后安排。这篇指南会把需要知道的事情讲清楚。
            </p>
          </Reveal>

          <Reveal delay={0.08}>
            <nav className="mt-10 rounded-2xl border border-ink/10 bg-white p-6" aria-label="Table of contents" data-testid="blog-toc">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-moss">本文目录</p>
              <ol className="mt-3 grid gap-2 sm:grid-cols-2">
                {TOC.map((t, i) => (
                  <li key={t}>
                    <a href={`#ds-${i + 1}`} className="text-sm text-ink/65 underline-offset-4 hover:text-forest hover:underline">
                      {String(i + 1).padStart(2, "0")}. {t}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </Reveal>

          <div className="mt-14 space-y-14">
            <Section id="ds-1" num="01" title="什么是 TikTok Shop 一件代发？">
              <p>
                一件代发通常是指：卖家获得订单后，由供应或履约合作方按照订单安排商品处理和发货，
                卖家不一定需要自己长期囤大量库存。
              </p>
              <p>
                但具体合作方式会因产品、供应商、库存安排、物流、售后和平台政策而不同。
                有些模式仍需要你准备少量样品或安全库存，有些则由履约方管理库存。
                所以「一件代发」不是一个统一定义，而是一组需要逐项确认的合作安排。
              </p>
            </Section>

            <Section id="ds-2" num="02" title="一件代发和传统囤货有什么不同？">
              <div className="overflow-x-auto rounded-2xl border border-ink/10">
                <table className="w-full min-w-[520px] border-collapse bg-white text-sm" data-testid="comparison-table">
                  <thead>
                    <tr className="bg-forest text-paper">
                      {COMPARISON.headers.map((h) => (
                        <th key={h} className="px-4 py-3 text-left font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARISON.rows.map((r) => (
                      <tr key={r[0]} className="border-t border-ink/8">
                        <td className="px-4 py-3 font-semibold text-ink">{r[0]}</td>
                        <td className="px-4 py-3 text-ink/65">{r[1]}</td>
                        <td className="px-4 py-3 text-ink/65">{r[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p>
                没有哪一种一定比较好。不同商家适合不同方式 —— 关键是你的资金、产品类型、
                团队人手和想测试的规模。
              </p>
            </Section>

            <Section id="ds-3" num="03" title="哪些商家可能适合了解一件代发？">
              <ul className="space-y-3">
                {SUITABLE.map((s) => (
                  <li key={s.t} className="flex gap-3">
                    <Check size={17} className="mt-1 shrink-0 text-moss" />
                    <span><strong className="text-ink">{s.t}</strong> — {s.d}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section id="ds-4" num="04" title="哪些情况不一定适合？">
              <ul className="space-y-3">
                {NOT_SUITABLE.map((s) => (
                  <li key={s} className="flex gap-3">
                    <X size={17} className="mt-1 shrink-0 text-clay" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              <p>
                如果你的产品属于以上几类，不代表不能做 TikTok Shop，而是需要更谨慎地评估合作方式，
                或者先解决供应和履约问题再开始。
              </p>
            </Section>

            <Section id="ds-5" num="05" title="TikTok Shop 一件代发通常怎样运作？">
              <ol className="space-y-4">
                {STEPS.map((s, i) => (
                  <li key={s.t} className="flex gap-4 rounded-2xl border border-ink/10 bg-white p-5">
                    <span className="font-display text-xl italic text-moss">0{i + 1}</span>
                    <div>
                      <p className="font-bold text-ink">{s.t}</p>
                      <p className="mt-1 text-sm text-ink/60">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Section>

            <Section id="ds-6" num="06" title="无需囤货是不是完全不用资金？">
              <p>
                <strong className="text-ink">不是。</strong>即使采用无需自行囤货模式，也可能涉及：
                平台相关成本、内容制作、运营、广告、样品、服务、物流、售后，以及其他业务成本。
              </p>
              <p>
                把「不用囤货」当成「不用花钱」，是最常见的误解。开始之前，先把所有成本项目列出来算一遍。
              </p>
            </Section>

            <Section id="ds-7" num="07" title="一件代发最重要的不是「没有库存」">
              <p>很多人把注意力放在「不用囤货」，但真正决定成败的是这些：</p>
              <div className="grid gap-2.5 sm:grid-cols-2">
                {KEY_POINTS.map((k) => (
                  <p key={k} className="flex items-center gap-2 rounded-xl bg-sand px-4 py-2.5 text-sm font-medium text-ink/80">
                    <span className="h-1.5 w-1.5 rounded-full bg-lime" />
                    {k}
                  </p>
                ))}
              </div>
              <p>库存只是表面，履约能力才是核心。</p>
            </Section>

            <Section id="ds-8" num="08" title="TikTok Shop 一件代发有哪些风险？">
              <ul className="space-y-2.5">
                {RISKS.map((r) => (
                  <li key={r} className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-clay" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
              <p>这些风险不代表不能做，而是代表合作前要把责任分工和应对方案谈清楚。</p>
            </Section>

            <Section id="ds-9" num="09" title="怎样判断产品适不适合 TikTok Shop？">
              <p>不要简单地问「可不可以做」，而是逐项看实际因素：</p>
              <div className="grid gap-2.5 sm:grid-cols-2">
                {PRODUCT_FACTORS.map((f) => (
                  <p key={f} className="flex items-center gap-2 rounded-xl border border-ink/10 bg-white px-4 py-2.5 text-sm font-medium text-ink/80">
                    <Check size={14} className="text-moss" />
                    {f}
                  </p>
                ))}
              </div>
              <p>
                我们不会说「任何产品都适合」。如果你的产品还没想清楚，可以先
                <Link to="/contact" className="font-semibold text-forest underline-offset-4 hover:underline"> 在线咨询</Link>，
                告诉我们你卖什么，我们会给你直接的看法。
              </p>
            </Section>

            <Section id="ds-10" num="10" title="一件代发和代运营有什么区别？">
              <p>
                一件代发主要涉及<strong className="text-ink">产品供应、库存和履约模式</strong>；
                代运营主要涉及<strong className="text-ink">店铺管理、商品优化、内容、Campaign 和日常运营</strong>。
              </p>
              <p>
                两者可以同时存在，但不是同一个概念。例如：你可以用一件代发解决库存和发货，
                同时让团队协助<Link to="/tiktok-shop-management" className="font-semibold text-forest underline-offset-4 hover:underline">店铺日常运营</Link>、
                <Link to="/product-optimisation" className="font-semibold text-forest underline-offset-4 hover:underline">商品页面</Link>和
                <Link to="/content-strategy" className="font-semibold text-forest underline-offset-4 hover:underline">内容方向</Link>。
                分工清楚，生意才不会乱。
              </p>
            </Section>

            <Section id="ds-11" num="11" title="TKShopMY 可以协助什么？">
              <p>TKShopMY 可以根据你的实际情况协助：</p>
              <ul className="space-y-2.5">
                {[
                  { t: "TikTok Shop 开店准备", to: "/tiktok-shop-setup" },
                  { t: "产品与店铺评估", to: "/services" },
                  { t: "商品页面与上架", to: "/product-optimisation" },
                  { t: "内容方向规划", to: "/content-strategy" },
                  { t: "日常运营支持", to: "/tiktok-shop-management" },
                  { t: "一件代发 / 无需自行囤货合作方式咨询", to: "/faq" },
                ].map((l) => (
                  <li key={l.t}>
                    <Link to={l.to} className="flex items-center gap-2 font-medium text-forest underline-offset-4 hover:underline">
                      <span className="h-1.5 w-1.5 rounded-full bg-lime" />
                      {l.t}
                    </Link>
                  </li>
                ))}
              </ul>
              <p>
                我们会先了解你的产品和情况，再说明适合考虑的方式。TKShopMY 不承诺保证审批、
                保证出单或保证销售额 —— 是否适合，需要根据产品和实际合作条件判断。
              </p>
            </Section>
          </div>

          {/* GEO Fact Block */}
          <Reveal>
            <div className="mt-16 rounded-2xl border border-forest/20 bg-white p-7" data-testid="fact-block">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-moss">快速了解</p>
              <div className="mt-5 space-y-5">
                <div>
                  <h2 className="text-base font-bold text-ink">关于 TikTok Shop 一件代发</h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink/65">
                    TikTok Shop 一件代发是一种订单履约合作方式，卖家不一定需要自行长期持有大量库存。
                    具体模式会根据产品、供应商、物流、售后和平台规则而不同。
                  </p>
                </div>
                <div>
                  <h2 className="text-base font-bold text-ink">TKShopMY 是什么？</h2>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink/65">
                    TKShopMY 是服务马来西亚市场的独立电商运营与商家支持团队，提供 TikTok Shop 开店、
                    商品优化、内容方向、运营及不同合作模式咨询。TKShopMY 与 TikTok 官方没有任何关联。
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          {/* FAQ */}
          <div className="mt-16">
            <SectionTag>常见问题</SectionTag>
            <h2 className="font-sans text-2xl font-bold tracking-tight sm:text-3xl">关于一件代发的常见问题</h2>
            <div className="mt-8">
              <FaqAccordion faqs={FAQS} testIdPrefix="ds-faq" />
            </div>
          </div>

          {/* Final CTA */}
          <Reveal>
            <div className="mt-16 rounded-3xl bg-forest p-8 text-center text-paper md:p-12 grain relative overflow-hidden" data-testid="ds-final-cta">
              <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-lime/15 blur-3xl" />
              <h2 className="font-sans text-2xl font-bold leading-snug tracking-tight text-balance sm:text-3xl">
                想知道自己的产品适不适合一件代发？
              </h2>
              <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-paper/70 md:text-base">
                告诉我们你目前卖什么产品、在哪里销售，以及你想怎样开始。
                TKShopMY 可以先了解你的情况，再说明适合考虑的 TikTok Shop 合作方式。
              </p>
              <div className="mt-7">
                <ChatButton location="blog_dropshipping_final" variant="lime" testId="ds-final-chat-button">
                  WhatsApp 咨询我的情况
                </ChatButton>
              </div>
              <p className="mt-4 text-xs text-paper/50">免费初步咨询</p>
            </div>
          </Reveal>
        </div>
      </article>
    </>
  );
}
